// Per-user sim funnel counts for the Simulations page Users card and the High
// Click Users drill, windowed to the selected period (BD-1003). The server
// user_roster aggregation windows these counts; the risk score stays the fixed
// 180-day snapshot.
import useDashboardData from './hooks/useDashboardData';
import { useExcludeDisabled } from './disabledFilter';

const normScope = (v) => (v == null ? undefined : Array.isArray(v) ? (v.length ? v : undefined) : [v]);
const rate = (n, sent) => (sent ? Math.round((n / sent) * 100) : 0);

export const CLICK_USERS_LIMIT = 5000;

export function mapRosterClickRow(r) {
    const sent = r.simsSent || 0;
    const clicked = r.simsClicked || 0;
    const reported = r.simsReported || 0;
    return {
        name: `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.email,
        email: r.email, dept: r.dept || '', location: r.location || '',
        risk: r.riskScore ?? null,
        sent, clicked, secondStage: r.simsLanded || 0, reported,
        clickRate: rate(clicked, sent), reportRate: rate(reported, sent),
    };
}

// Same row shape from a local (already windowed and scoped) send log.
export function buildClickUserStats(log, riskByEmail) {
    const byEmail = new Map();
    for (const e of log || []) {
        if (!e.email) continue;
        const u = byEmail.get(e.email) || {
            name: `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.email,
            email: e.email, dept: e.dept || '', location: e.location || '',
            risk: riskByEmail?.get(e.email) ?? null,
            sent: 0, clicked: 0, secondStage: 0, reported: 0,
        };
        u.sent += 1;
        if (e.status === 'clicked' || e.status === 'clicked_2nd') u.clicked += 1;
        if (e.status === 'clicked_2nd') u.secondStage += 1;
        if (e.status === 'reported') u.reported += 1;
        byEmail.set(e.email, u);
    }
    return [...byEmail.values()].map((u) => ({ ...u, clickRate: rate(u.clicked, u.sent), reportRate: rate(u.reported, u.sent) }));
}

// Server rows (null while loading, unavailable, or when a user scope holds display
// names the server can't match). Card and drill pass the same args so they share
// one cached request.
export function useClickUsers({ scope, period, startDate, endDate }, { skip = false } = {}) {
    const excludeDisabled = useExcludeDisabled();
    const s = scope || {};
    const serverScope = {
        department: normScope(s.department), location: normScope(s.location), city: normScope(s.city), country: normScope(s.country),
        state: normScope(s.state), company: normScope(s.company), adGroup: normScope(s.adGroup), user: normScope(s.user), tenancy: normScope(s.tenancy),
    };
    const hasServerScope = Object.values(serverScope).some(Boolean);
    const userScopeIsEmails = !serverScope.user || serverScope.user.every((v) => String(v).includes('@'));
    const q = useDashboardData({
        metric: 'users', aggregation: 'user_roster', limit: CLICK_USERS_LIMIT,
        sort: { field: 'clickRate', direction: 'desc' },
        includeDisabled: !excludeDisabled,
        scope: hasServerScope ? serverScope : undefined,
        period, startDate, endDate,
    }, { skip: skip || !userScopeIsEmails });
    const roster = userScopeIsEmails && Array.isArray(q.data?.roster) ? q.data.roster : null;
    return roster ? roster.map(mapRosterClickRow).filter((u) => u.sent > 0) : null;
}
